import { usePathname, useRouter } from 'next/navigation';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { ButtonVariant } from '@/components/button';
import Typography from '@/components/typography';
import { PLAT_MAP_URL } from '@/constants/urls';
import { CardCloseButton } from '@/features/selectionsPanel/selectionCard/baseCard/cardCloseButton';
import { CardFooterButton } from '@/features/selectionsPanel/selectionCard/baseCard/cardFooterButton';
import { CardPill } from '@/features/selectionsPanel/selectionCard/baseCard/cardPill';
import { IRealEstateFeature } from '@/models/features';
import { removeFeatureByFeatureId } from '@/redux/slices/featureSlice';
import { convertMoney } from '@/utils/common';

import { useGenericSelectionPanelFunctions } from '../../hooks/useGenericSelectionPanelFunctions';
import { formatAcres } from '../../util/determineSelectionsPanelAcres';
import { Card } from '../baseCard/card';
import { LineItem } from '../baseCard/lineItem';

type Props = {
  feature: IRealEstateFeature;
  index: number;
};

export const ListingCard: FC<Props> = ({ feature, index }) => {
  const router = useRouter();
  const pathname = usePathname();
  const { price_per_acre, total_acres, list_price, state, county } =
    feature.properties;
  const dispatch = useDispatch();
  const [isHovered, setIsHovered] = useState(false);

  const {
    handleMouseOn,
    handleMouseOff,
    goToGeometryLocation,
    openRealEstate,
  } = useGenericSelectionPanelFunctions();

  const isAlreadyOnListingDetailsPage = useMemo(() => {
    if (!feature || !pathname) return false;

    return pathname.includes(String(feature.id));
  }, [pathname, feature]);

  const onClose = useCallback(() => {
    dispatch(removeFeatureByFeatureId(feature));
  }, [dispatch, feature]);

  const handleOnClick = useCallback(() => {
    goToGeometryLocation(feature);
  }, [feature, goToGeometryLocation]);

  const handleViewDetails = useCallback(() => {
    return openRealEstate(feature);
  }, [feature, openRealEstate]);

  const handleHideDetails = useCallback(() => {
    router.push(PLAT_MAP_URL);
  }, [router]);

  useEffect(() => {
    if (isHovered) {
      handleMouseOn(feature);
    } else {
      handleMouseOff(feature);
    }
  }, [feature, handleMouseOff, handleMouseOn, isHovered]);

  const cardPillColorClass = useMemo(() => {
    return (
      {
        available: 'bg-jade',
        'sale-pending': 'bg-listings-sale-pending',
        sold: 'bg-listings-sold',
      }[feature.properties.status] ?? ''
    );
  }, [feature.properties.status]);

  const pricePerAcre = useMemo(() => {
    if (price_per_acre) {
      return convertMoney(Number(price_per_acre));
    }
    return '-';
  }, [price_per_acre]);

  return (
    <Card
      onClick={handleOnClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="flex items-center gap-2 pb-2">
        <CardPill
          className={cardPillColorClass}
          content={pricePerAcre}
          index={index}
        />
        <Typography className="body2">/acre</Typography>
        <CardCloseButton className=" ml-auto" onClick={onClose} />
      </div>
      <LineItem label="Total Price" value={convertMoney(Number(list_price))} />
      <LineItem label="Acres" value={formatAcres(Number(total_acres), false)} />
      <LineItem label="Location" value={`${county}, ${state}`} />
      {isAlreadyOnListingDetailsPage ? (
        <CardFooterButton
          text="Hide Details"
          variant={ButtonVariant.PRIMARY_LIGHT}
          onClick={handleHideDetails}
        />
      ) : (
        <CardFooterButton text="View Details" onClick={handleViewDetails} />
      )}
    </Card>
  );
};
